<template>
    <div>
        <!-- Marcador LAeq unificado: nombre + circulo + valor + hora -->
        <l-marker
            v-if="hasLaeqData"
            :lat-lng="position"
            :icon="laeqIcon"
            :zIndexOffset="1000"
            @click="handleClick"
        />

        <!-- Marcador con icono de router (sin datos) -->
        <l-marker
            v-else
            :lat-lng="position"
            :icon="customIcon"
            @click="handleClick"
        >
            <l-tooltip :content="label" />
        </l-marker>
    </div>
</template>

<script>
import { LMarker, LTooltip } from 'vue2-leaflet'
import L from 'leaflet'
import moment from 'moment'

export default {
    components: {
        LMarker,
        LTooltip
    },

    props: {
        map: {
            type: Object,
            required: true
        },
        bounds: {
            type: Object,
            required: true
        },
        marker: {
            type: Object,
            required: true
        },
        deviceInfo: {
            type: Object,
            default: null
        },
        label: {
            type: String,
            default: ''
        }
    },

    computed: {
        position() {
            return [this.marker.location.lat, this.marker.location.lng]
        },

        hasLaeqData() {
            return this.getLaeqValue() !== null
        },

        fillColor() {
            const laeq = this.getLaeqValue()
            if (laeq === null || laeq === undefined || !this.deviceInfo) return '#9E9E9E'
            return this.getLaeqColor(laeq)
        },

        laeqTextColor() {
            const laeq = this.getLaeqValue()
            if (laeq === null || laeq === undefined) return '#111'
            return laeq < 60 ? '#111' : '#fff'
        },

        laeqDisplayValue() {
            const laeq = this.getLaeqValue()
            if (laeq === null || laeq === undefined || Number.isNaN(Number(laeq))) return '--'
            return Number(laeq).toFixed(1)
        },

        markerTimeLabel() {
            if (!this.deviceInfo || !this.deviceInfo.ts) return '--:--'
            const parsed = moment(this.deviceInfo.ts)
            return parsed.isValid() ? parsed.format('HH:mm') : '--:--'
        },

        laeqIcon() {
            const deviceName = this.escapeHtml(this.label || 'Dispositivo')
            const valueLabel = this.escapeHtml(this.laeqDisplayValue)
            const timeLabel = this.escapeHtml(this.markerTimeLabel)

            const html = `
                <div class="sa-laeq-marker" style="--sa-marker-fill:${this.fillColor}; --sa-marker-text:${this.laeqTextColor};">
                    <div class="sa-laeq-marker__name">${deviceName}</div>
                    <div class="sa-laeq-marker__bubble">
                        <span class="sa-laeq-marker__value">${valueLabel}</span>
                    </div>
                    <div class="sa-laeq-marker__time">${timeLabel}</div>
                </div>
            `
            return L.divIcon({
                html: html,
                className: 'sa-laeq-marker-host',
                iconSize: [144, 124],
                iconAnchor: [72, 62]
            })
        },

        customIcon() {
            return L.icon({
                iconUrl: '/routerfond.png',
                iconSize: [40, 40],
                iconAnchor: [20, 40],
                popupAnchor: [0, -40]
            })
        },
    },

    methods: {
        getLaeqColor(laeq) {
            if (laeq < 35) return '#A0BABF'
            if (laeq < 40) return '#A0BABF'
            if (laeq < 45) return '#B8D6D1'
            if (laeq < 50) return '#CEE4CC'
            if (laeq < 55) return '#E2F2BF'
            if (laeq < 60) return '#F3C683'
            if (laeq < 65) return '#E87E4D'
            if (laeq < 70) return '#CD463E'
            if (laeq < 75) return '#A11A4D'
            if (laeq < 80) return '#75085C'
            return '#430A4A'
        },

        getLaeqValue() {
            if (!this.deviceInfo) return null
            if (!this.deviceInfo.sensors) return null

            const laeqSensor = this.deviceInfo.sensors.find(s => s.sensorType === 'laeq')
            if (!laeqSensor) return null

            const numeric = Number(laeqSensor.value)
            if (Number.isNaN(numeric)) return null
            return numeric
        },

        handleClick() {
            this.$emit('selectMarker', this.marker, null)
        },

        escapeHtml(value) {
            return String(value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;')
        },

        updateBounds() {
            if (this.bounds && this.position) {
                this.bounds.extend(this.position)
            }
        }
    },

    mounted() {
        this.updateBounds()
    },

    watch: {
        position() {
            this.updateBounds()
        },
        deviceInfo(newVal) {
            if (newVal) {
                this.$forceUpdate()
            }
        }
    }
}
</script>

<style>
.sa-laeq-marker-host {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
}

.sa-laeq-marker {
    width: 144px;
    height: 124px;
    position: relative;
    pointer-events: auto;
}

.sa-laeq-marker__name {
    position: absolute;
    left: 50%;
    top: 0;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.82);
    color: #fff;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    border: 1px solid rgba(255, 255, 255, 0.45);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.35);
}

.sa-laeq-marker__bubble {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 46px;
    height: 46px;
    border-radius: 999px;
    background: var(--sa-marker-fill);
    border: 3px solid #fff;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.32);
    display: flex;
    align-items: center;
    justify-content: center;
}

.sa-laeq-marker__value {
    font-size: 13px;
    line-height: 1;
    font-weight: 800;
    color: var(--sa-marker-text);
}

.sa-laeq-marker__time {
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.72);
    color: #fff;
    padding: 2px 8px;
    border-radius: 3px;
    font-size: 10px;
    line-height: 1;
    white-space: nowrap;
    border: 1px solid rgba(255, 255, 255, 0.32);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.28);
}

@media (max-width: 960px) {
    .sa-laeq-marker__name {
        font-size: 10px;
        padding: 3px 8px;
    }

    .sa-laeq-marker__bubble {
        width: 40px;
        height: 40px;
    }

    .sa-laeq-marker__value {
        font-size: 12px;
    }

    .sa-laeq-marker__time {
        font-size: 9px;
    }
}

@media (max-width: 600px) {
    .sa-laeq-marker__name {
        max-width: 96px;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .sa-laeq-marker__bubble {
        width: 36px;
        height: 36px;
        border-width: 2px;
    }

    .sa-laeq-marker__value {
        font-size: 11px;
    }

    .sa-laeq-marker__time {
        padding: 2px 6px;
    }
}
</style>
